/**
 * PKCE environment detection utilities.
 *
 * This is the single source of truth for environment detection and PKCE requirement checks.
 * Intentionally zero-dependency so it can be imported at application entry points
 * without pulling in heavy bundles.
 *
 * Consumers:
 *   - applications/aw-advisor/src/index.ts (bootstrap gate)
 *   - @blk/aw-api-clients (re-exports for backward compatibility)
 */

type EnvironmentType = 'dev' | 'tst' | 'uat' | 'prod' | 'local';

type EnvironmentHostnameConfig = {
  type: EnvironmentType;
  hostnames: string[];
  requiresPkce: boolean;
};

/**
 * Environment hostname configuration
 * Maps environment types to hostname patterns and PKCE requirements
 *
 * Pattern matching strategy:
 * - Uses full hostname matching for better reliability
 * - Supports multiple hostnames per environment
 * - Inspired by ApiUtils.ts pattern
 */
export const ENVIRONMENT_HOSTNAME_CONFIG: readonly EnvironmentHostnameConfig[] = Object.freeze([
  {
    type: 'local',
    hostnames: ['localhost', '127.0.0.1'],
    requiresPkce: false,
  },
  {
    type: 'dev',
    hostnames: ['devc.blackrock.com', 'azdevdmz.blackrock.com'],
    requiresPkce: true,
  },
  {
    type: 'tst',
    hostnames: ['testc.blackrock.com', 'aztstdmz.blackrock.com'],
    requiresPkce: true,
  },
  {
    type: 'uat',
    hostnames: ['azrc.blackrock.com'],
    requiresPkce: true,
  },
  {
    type: 'prod',
    hostnames: ['www.blackrock.com', 'azblkdmz.blackrock.com'],
    requiresPkce: true,
  },
]);

/**
 * Test if the current hostname matches any of the given hostnames
 * Matches exactly or as a dot-delimited subdomain suffix (never a bare substring),
 * so an attacker-controlled hostname cannot spoof a trusted environment by merely
 * containing a trusted pattern (TVAS-4038105, CWE-183).
 * @param patterns - Array of exact hostnames to test against
 * @returns true if the current hostname exactly matches or is a subdomain of any of the patterns
 */
const testHostname = (patterns: string[]): boolean => {
  const hostname = window?.location?.hostname ?? '';
  return patterns.some((pattern) => hostname === pattern || hostname.endsWith(`.${pattern}`));
};

const hasExternalApiHost = (): boolean => {
  if (typeof process === 'undefined') {
    return false;
  }

  return !!process.env.VITE_EXTERNAL_API_HOST;
};

export const isLocalTestAgraphEnabled = (): boolean => {
  const localFlagEnabled = globalThis.localStorage?.getItem('testExternalAgraph') === 'true';
  return localFlagEnabled || hasExternalApiHost();
};

/**
 * Detect the current environment based on hostname
 *
 * Matches the current window.location.hostname against configured environment hostnames.
 *
 * @returns The detected environment type ('local', 'dev', 'tst', 'uat', or 'prod'), or undefined if hostname doesn't match any configured environment
 */
export const detectEnvironment = (): EnvironmentType | undefined => {
  for (const envConfig of ENVIRONMENT_HOSTNAME_CONFIG) {
    if (testHostname(envConfig.hostnames)) {
      return envConfig.type;
    }
  }
  return undefined;
};

/**
 * Check if PKCE authentication is required for the current environment
 *
 * Determines PKCE requirement based on the detected environment's configuration.
 * For localhost, this depends on the 'testExternalAgraph' localStorage flag checked dynamically.
 *
 * @returns true if PKCE auth is required for the detected environment, false if environment is not detected or PKCE is not required
 */
export const isPkceRequired = (): boolean => {
  const currentEnv = detectEnvironment();
  if (!currentEnv) {
    return false;
  }

  // For local environment, check localStorage dynamically
  if (currentEnv === 'local') {
    return isLocalTestAgraphEnabled();
  }

  const envConfig = ENVIRONMENT_HOSTNAME_CONFIG.find((config) => config.type === currentEnv);
  return envConfig?.requiresPkce ?? false;
};

/**
 * Check if the current environment is a cloud environment
 * @returns true if the environment is 'dev', 'tst', 'uat', or 'prod'; false otherwise
 */
export const isCloud = (): boolean => {
  const env = detectEnvironment();
  return env === 'dev' || env === 'tst' || env === 'uat' || env === 'prod' || isLocalTestAgraphEnabled();
};
